import { z } from 'zod';

// Zod schemas for validation
export const ChatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  timestamp: z.string(),
  modelId: z.string().optional()
});

export const ChatRequestSchema = z.object({
  modelId: z.string(),
  responseId: z.string().optional(),
  message: z.string(),
  context: z.string()
});

export const ChatResponseSchema = z.object({
  messageId: z.string(),
  content: z.string(),
  modelId: z.string(),
  timestamp: z.string(),
  tokenUsage: z.object({
    totalTokens: z.number(),
    inputTokens: z.number().optional(),
    outputTokens: z.number().optional()
  }).optional(),
  responseTime: z.number().optional()
});

// Types derived from schemas
export type ChatMessage = z.infer<typeof ChatMessageSchema>;
export type ChatRequest = z.infer<typeof ChatRequestSchema>;
export type ChatResponse = z.infer<typeof ChatResponseSchema>;

export interface ChatSession {
  id: string;
  messages: ChatMessage[];
  modelId: string;
  responseId?: string;
  context: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Send a chat message to continue conversation with the AI
 */
export async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  try {
    // Validate request
    const validatedRequest = ChatRequestSchema.parse(request);

    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(validatedRequest)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Chat request failed' }));
      throw new Error(errorData.error || `Chat request failed with status ${response.status}`);
    }

    const data: ChatApiResponse<ChatResponse> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Chat request failed');
    }

    if (!data.data) {
      throw new Error('No chat response data received');
    }

    // Validate response
    return ChatResponseSchema.parse(data.data);
  } catch (error) {
    console.error('Chat API error:', error);
    throw error instanceof Error ? error : new Error('Chat request failed');
  }
}

/**
 * Create a new user message for optimistic UI updates
 */
export function createUserMessage(content: string): ChatMessage {
  return {
    id: `user-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    role: 'user',
    content,
    timestamp: new Date().toISOString()
  };
}

/**
 * Create an assistant message from chat response
 */
export function createAssistantMessage(response: ChatResponse): ChatMessage {
  return {
    id: response.messageId,
    role: 'assistant',
    content: response.content,
    timestamp: response.timestamp,
    modelId: response.modelId
  };
}

/**
 * Create a loading/pending message for UI feedback
 */
export function createLoadingMessage(modelId: string): ChatMessage {
  return {
    id: `loading-${Date.now()}`,
    role: 'assistant',
    content: '...',
    timestamp: new Date().toISOString(),
    modelId
  };
}

/**
 * Format context from response text for chat API
 */
export function formatChatContext(responseText: string, maxLength: number = 2000): string {
  if (responseText.length <= maxLength) {
    return responseText;
  }

  // Truncate and add ellipsis
  return responseText.substring(0, maxLength - 3) + '...';
}

/**
 * Mock chat API - replaced with clear "not implemented" message
 * TODO: Implement real chat API endpoint
 */
export async function mockChatMessage(request: ChatRequest): Promise<ChatResponse> {
  console.warn('[mockChatMessage] Chat API not yet implemented - returning placeholder message');

  // Simulate brief network delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const placeholderMessage =
    "💭 Chat feature coming soon!\n\n" +
    "This interactive chat functionality is currently in development. " +
    "Once the chat API is implemented, you'll be able to have real conversations " +
    "with the AI models about their responses.\n\n" +
    "For now, you can review the full response above.";

  return {
    messageId: `placeholder-${Date.now()}`,
    content: placeholderMessage,
    modelId: request.modelId,
    timestamp: new Date().toISOString(),
    tokenUsage: {
      totalTokens: 0,
      inputTokens: 0,
      outputTokens: 0
    },
    responseTime: 0
  };
}